import { Pencil, Trash2, PiggyBank, ExternalLink, Lock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "@/hooks/useTranslation";
import type { GiftFund } from "@/services/api/eventSettings";
import type { GiftItem } from "@/services/api/gifts";
import { cn } from "@/lib/utils";
import { USER_ROUTES } from "@/routes";

interface GiftItemCardProps {
  item: GiftFund | GiftItem;
  type: "fund" | "gift";
  locale: string;
  onToggle?: (enabled: boolean) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  toggling?: boolean;
  deleting?: boolean;
  /** When true, gift is locked due to free plan - disable all actions */
  isLocked?: boolean;
}

export function GiftItemCard({
  item,
  type,
  locale,
  onToggle,
  onEdit,
  onDelete,
  toggling = false,
  deleting = false,
  isLocked = false,
}: GiftItemCardProps) {
  const { t } = useTranslation();

  const formatCurrency = (value: number, currency: string) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return `${value.toLocaleString()} ${currency}`;
    }
  };

  const isFund = type === "fund";
  const fund = isFund ? (item as GiftFund) : null;
  const gift = !isFund ? (item as GiftItem) : null;

  const enabled = isFund ? fund!.enabled : gift!.enabled;
  const currency = isFund ? fund!.currency : gift!.currency;
  const requiresBankLink = isFund ? fund!.requiresBankLink : false;

  return (
    <Card className="border shadow-sm group">
      <CardContent className="p-6 space-y-4">
        {/* Header row: title + actions (top-aligned) */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <div className="flex items-start gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-foreground leading-none">
                {isFund ? fund!.name : gift!.name}
              </h3>
              {!isFund && gift!.category && (
                <Badge variant="outline" className="text-xs">
                  {gift!.category.name}
                </Badge>
              )}
            </div>

            {isFund ? (
              fund!.description && (
                <p className="text-sm text-muted-foreground line-clamp-3">{fund!.description}</p>
              )
            ) : (
              gift!.description && (
                <p className="text-sm text-muted-foreground line-clamp-3">{gift!.description}</p>
              )
            )}
          </div>

          <div className="flex items-start gap-2 shrink-0">
            {/* Lock indicator for free plan */}
            {isLocked && (
              <div className="flex items-center gap-1 text-muted-foreground" title={t("events.plan.upgradeRequired")}>
                <Lock className="h-4 w-4" />
              </div>
            )}

            {onToggle && (
              <Switch
                checked={enabled}
                onCheckedChange={onToggle}
                disabled={toggling || isLocked}
                className="data-[state=checked]:bg-pink-500"
                title={isLocked ? t("events.plan.upgradeRequired") : undefined}
              />
            )}

            <div className={cn(
              "transition-opacity flex items-center gap-2",
              // JUB-39 (R4): no hover on touch screens — keep the actions visible
              // there, and reveal them on keyboard focus as well as on hover.
              isLocked
                ? "opacity-50"
                : "opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 [@media(pointer:coarse)]:opacity-100"
            )}>
              {onEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 bg-background/90 backdrop-blur-sm hover:bg-background"
                  onClick={onEdit}
                  disabled={isLocked}
                  title={isLocked ? t("events.plan.upgradeRequired") : undefined}
                  aria-label={
                    isFund
                      ? t("gifts.fund.edit")
                      : t("events.form.editGift")
                  }
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {onDelete && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 bg-background/90 backdrop-blur-sm hover:bg-background text-destructive hover:text-destructive"
                  onClick={onDelete}
                  disabled={deleting || isLocked}
                  title={isLocked ? t("events.plan.upgradeRequired") : undefined}
                  aria-label={
                    isFund
                      ? t("gifts.fund.delete")
                      : t("events.form.deleteGift")
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Cover Image */}
        {(isFund ? fund!.cover : gift!.cover) && (
          <div className="w-full h-48 rounded-lg overflow-hidden">
            <img
              src={(isFund ? fund!.cover : gift!.cover) ?? undefined}
              alt={isFund ? fund!.name : gift!.name}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Cover Image */}

        {/* Goal and Total Contributions */}
        {isFund ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t("gifts.fund.raisedSoFar")}
              </span>
              <span className="font-medium text-foreground">
                {fund!.goal > 0
                  ? `${formatCurrency(fund!.totalContributions, currency)} / ${formatCurrency(fund!.goal, currency)}`
                  : formatCurrency(fund!.totalContributions, currency)}
              </span>
            </div>
            {fund!.goal > 0 && (
              <Progress value={Math.min((fund!.totalContributions / fund!.goal) * 100, 100)} />
            )}
          </div>
        ) : gift!.amount > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t("events.form.giftProgress") || t("gifts.fund.raisedSoFar")}
              </span>
              <span className="font-medium text-foreground">
                {formatCurrency(gift!.totalContributions, currency)} /{" "}
                {formatCurrency(gift!.amount, currency)}
              </span>
            </div>
            <Progress
              value={Math.min((gift!.totalContributions / gift!.amount) * 100, 100)}
            />
          </div>
        ) : null}

        {/* Bank Link Warning - slimmer visual */}
        {isFund && requiresBankLink && (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/30 px-3 py-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <PiggyBank className="h-4 w-4 text-pink-500" />
              <span className="text-xs">
                {t("gifts.fund.linkBank")}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-3 text-xs"
              onClick={() => {
                window.location.href = USER_ROUTES.connectedAccounts(locale);
              }}
            >
              {t("gifts.fund.openPaymentSettings")}
            </Button>
          </div>
        )}

        {/* View Link - for gift items */}
        {!isFund && gift!.link && (
          <div>
            <a
              href={gift!.link!}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-start w-full h-9 px-3 rounded-md font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {t("events.form.viewLink")}
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

